import React, { useEffect, useRef } from "react";
import {
  Target,
  Users,
  FlaskConical,
  Rocket,
  TrendingUp,
  Loader2,
  CheckCircle,
  BarChart3,
  MapPin,
  Zap,
  DollarSign,
} from "lucide-react";
import { Steps, ConfigProvider } from "antd";
import type { StepsProps } from "antd";
import "./PerformanceAgentSteps.css";
import PowerAnalysisExperiment from "./PowerAnalysisExperiment";
import ViewResultsMeasurePerformance from "./ViewResultsMeasurePerformance";
import AudienceDistribution from "./AudienceDistribution";
import SaturationCurves from "./SaturationCurves";
import SpendEffectByChannel from "./SpendEffectByChannel";
import { MMM_JOB_CONFIG_ID } from "../../../services/insightsService";

interface StepData {
  number: number;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

interface PerformanceAgentStepsProps {
  currentStep: number;
  allStepsData?: { [key: number]: any }; // All visualization data indexed by step number
  stepData?: {
    objective?: string;
    reason?: string;
    selectedSegments?: number;
    experimentEnabled?: boolean;
    experimentType?: string;
    campaignStatus?: string;
  };
}

const PerformanceAgentSteps: React.FC<PerformanceAgentStepsProps> = ({
  currentStep = 1, // Default to step 1 if not provided
  allStepsData = {},
  stepData = {},
}) => {
  // Refs for each step card to enable auto-scroll
  const stepRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Auto-scroll to the latest step when new data arrives
  useEffect(() => {
    // Get the highest step number that has data
    const stepsWithData = Object.keys(allStepsData).map(Number).filter(n => !isNaN(n));
    if (stepsWithData.length > 0) {
      const latestStep = Math.max(...stepsWithData);
      const stepElement = stepRefs.current[latestStep];

      if (stepElement) {
        // Get the sticky header height to offset scroll position
        const stickyHeader = document.querySelector('.sticky.top-0.z-40');
        const headerHeight = stickyHeader?.getBoundingClientRect().height || 0;

        // Smooth scroll to the latest step with offset for sticky header
        const elementPosition = stepElement.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight - 16; // 16px additional padding

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [allStepsData]); // Re-run when allStepsData changes

  const steps: StepData[] = [
    {
      number: 1,
      title: "PLAN",
      subtitle: "Campaign Context",
      icon: Target,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      number: 2,
      title: "OPTIMIZE",
      subtitle: "Audience Value",
      icon: Users,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      number: 3,
      title: "EXPERIMENT",
      subtitle: "Test Setup",
      icon: FlaskConical,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      number: 4,
      title: "ACT",
      subtitle: "Media Activation",
      icon: Rocket,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      number: 5,
      title: "MEASURE",
      subtitle: "Lift Analysis",
      icon: TrendingUp,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
    },
  ];

  // Convert to Ant Design Steps format - using numbers instead of icons
  const antSteps: StepsProps["items"] = steps.map((step) => {
    return {
      title: step.title,
      description: step.subtitle,
      // Ant Design automatically shows numbers when icon is not provided
    };
  });

  // Visualization rendering functions (moved from VisualizationPanel)
  const renderChannelRecommendations = (data: any) => {
    if (!data?.channels) return null;

    const channelData = Object.entries(data.channels).map(
      ([name, info]: [string, any]) => ({
        name,
        roi: info.mmm_roi,
        incrementality: info.sc_incrementality,
        current: info.current_spend,
        recommended: info.recommended_spend,
        lift: info.expected_lift,
      })
    );

    return (
      <div className="space-y-4 mt-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  Total Budget
                </p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                  ${(data.total_recommended_spend || 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                  Expected Lift
                </p>
                <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 mt-1">
                  ${(data.total_expected_lift || 0).toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-indigo-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Channel Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              Channel Recommendations (MMM + Synthetic Control)
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                    Channel
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                    ROI
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                    Recommended
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                    Expected Lift
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {channelData.map((channel) => (
                  <tr
                    key={channel.name}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {channel.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                      {channel.roi}x
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600 dark:text-blue-400">
                      ${channel.recommended.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-600 dark:text-indigo-400 font-medium">
                      ${channel.lift.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Saturation Curves and Spend vs. Effect */}
        <SaturationCurves jobId={MMM_JOB_CONFIG_ID} />
        <SpendEffectByChannel jobId={MMM_JOB_CONFIG_ID} />
      </div>
    );
  };

  const renderAudienceValueTable = (data: any) => {
    if (!data?.segments) return null;

    // Extract channel budgets from backend data (passed from Step 1)
    const channelBudgets = data.channel_budgets || {};

    // Use backend segments data - segments are organized by channel
    const backendSegments = data.segments || {};

    // Build universal segments list (all unique segments across channels)
    // Extract segment properties without channel-specific bid_adjustment
    const allSegmentsMap = new Map<string, any>();
    Object.values(backendSegments).forEach((channelSegments: any) => {
      channelSegments.forEach((segment: any) => {
        if (!allSegmentsMap.has(segment.name)) {
          // Store segment without bid_adjustment (universal properties only)
          const { bid_adjustment, ...universalProps } = segment;
          allSegmentsMap.set(segment.name, universalProps);
        }
      });
    });
    const universalSegments = Array.from(allSegmentsMap.values());

    // Build channels array with their segments
    const channels = Object.keys(backendSegments).map((channelName) => ({
      name: channelName,
      budget: channelBudgets[channelName] || 0,
      segments: backendSegments[channelName] || [],
    }));

    return (
      <div className="space-y-4 mt-6">
        {/* Audience Distribution by Dimension */}
        <AudienceDistribution data={data} />

        {/* Universal Segments Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="w-4 h-4 mr-2 text-blue-500" />
            Universal Audience Segments
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Cross-channel segments defined by geo × gender × age × device combinations
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/30">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                    Segment
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                    Dimensions
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                    LTV ($)
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                    Customers
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {universalSegments.map((segment, index) => (
                  <tr
                    key={segment.name || index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                      {segment.name || 'Unknown Segment'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                      {segment.dimensions
                        ? `${segment.dimensions.geo || ''} • ${segment.dimensions.gender || ''} • ${segment.dimensions.age || ''} • ${segment.dimensions.device || ''}`
                        : segment.description || '-'
                      }
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 font-semibold">
                      ${segment.ltv || segment.value || 0}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                      {(segment.customers || segment.size || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Channel Budget & Segment Value Rules */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
            Channel Budget Allocation & Segment Value Rules
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Budget allocated at channel level • Value rules applied at segment level
          </p>

          {channels.map((channel) => (
            <div key={channel.name} className="mb-6 last:mb-0">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                  {channel.name}
                  <span className="ml-2 text-xs text-gray-500">({channel.segments?.length || 0} segments)</span>
                </h5>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  Budget: ${channel.budget.toLocaleString()}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/30">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                        Segment
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                        LTV
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                        Customers
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                        Value Rule
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(channel.segments || []).map((segment: any, idx: number) => (
                      <tr
                        key={segment.name || idx}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                          {segment.name || 'Unknown Segment'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                          ${segment.ltv || segment.value || 0}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                          {(segment.customers || segment.size || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            segment.bid_adjustment === 0 || segment.bid_adjustment === '0%'
                              ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {segment.bid_adjustment || segment.value_rule || 'Base'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExperimentSetup = (data: any) => {
    if (!data) return null;

    // Show processing message if backend is still working (no jobConfigId yet, or explicit processing status)
    if (data.status === 'processing' || (!data.jobConfigId && !data.job_config_id && data.message)) {
      return (
        <div className="space-y-4 mt-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-700 p-6">
            <div className="flex items-center mb-4">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin mr-3" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Designing Experiment
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {data.message || "Calculating statistical power and selecting test markets..."}
            </p>
            {data.progress !== undefined && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${data.progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{data.progress}% complete</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // If data contains jobConfigId, use PowerAnalysisExperiment component
    if (data.jobConfigId || data.job_config_id) {
      return (
        <div className="mt-6">
          <PowerAnalysisExperiment
            jobConfigId={data.jobConfigId || data.job_config_id}
            mode={data.mode || "power_analysis"}
          />
        </div>
      );
    }

    // Otherwise, show basic experiment setup info
    return (
      <div className="space-y-4 mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2 text-purple-500" />
            Experiment Configuration
          </h4>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                Experiment Type
              </p>
              <p className="text-sm font-bold text-purple-900 dark:text-purple-100 mt-1">
                {data.type || "Channel Test"}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-700">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                Statistical Power
              </p>
              <p className="text-sm font-bold text-green-900 dark:text-green-100 mt-1">
                {data.power || "80"}%
              </p>
            </div>
          </div>

          {data.test_markets && (
            <div className="space-y-3">
              <div>
                <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <MapPin className="w-3 h-3 mr-1 text-blue-500" />
                  Test Markets
                </h5>
                <div className="space-y-1">
                  {data.test_markets.map((market: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-xs bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded"
                    >
                      <span className="text-gray-900 dark:text-white font-medium">
                        {market.name}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        Pop: {market.population}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <MapPin className="w-3 h-3 mr-1 text-gray-500" />
                  Control Markets
                </h5>
                <div className="space-y-1">
                  {data.control_markets?.map((market: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded"
                    >
                      <span className="text-gray-900 dark:text-white font-medium">
                        {market.name}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        Pop: {market.population}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActivationProgress = (data: any) => {
    if (!data) return null;

    const activationSteps = data.steps || [];

    return (
      <div className="space-y-4 mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-yellow-500" />
            Campaign Activation Status
          </h4>

          <div className="space-y-3">
            {activationSteps.map((step: any, idx: number) => (
              <div
                key={idx}
                className={`flex items-start p-3 rounded-lg ${
                  step.status === "completed"
                    ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700"
                    : step.status === "in_progress"
                    ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700"
                    : "bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 ${
                    step.status === "completed"
                      ? "bg-green-500"
                      : step.status === "in_progress"
                      ? "bg-blue-500"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                >
                  {step.status === "completed" ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  ) : (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {step.name}
                  </p>
                  {step.details && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {step.details}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMeasurementResults = (data: any) => {
    if (!data) return null;

    // Show processing message if backend is still working (no jobConfigId yet, or explicit processing status)
    if (data.status === 'processing' || (!data.jobConfigId && !data.job_config_id && data.message)) {
      return (
        <div className="space-y-4 mt-6">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700 p-6">
            <div className="flex items-center mb-4">
              <Loader2 className="w-6 h-6 text-purple-600 animate-spin mr-3" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Analyzing Measurement Results
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {data.message || "Processing campaign performance data and calculating incrementality..."}
            </p>
            {data.progress !== undefined && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${data.progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{data.progress}% complete</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // If data contains jobConfigId, use ViewResultsMeasurePerformance component
    if (data.jobConfigId || data.job_config_id) {
      return (
        <div className="mt-6">
          <ViewResultsMeasurePerformance
            jobConfigId={data.jobConfigId || data.job_config_id}
            mode={data.mode || "view_results"}
          />
        </div>
      );
    }

    // Otherwise, show basic measurement info
    return (
      <div className="space-y-4 mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-indigo-500" />
            Measurement Results
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {data.message || "Measurement results will be available after the campaign cooldown period."}
          </p>
        </div>
      </div>
    );
  };

  const renderStepDetails = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 1: Campaign Goal Analysis
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Analyzing your campaign based on selected objective and KPI.
            </p>
            {stepData?.objective || stepData?.reason ? (
              <>
                {stepData?.objective && (
                  <div className="mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Objective:{" "}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {stepData.objective}
                    </span>
                  </div>
                )}
                {stepData?.reason && (
                  <div className="mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      KPI:{" "}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {stepData.reason}
                    </span>
                  </div>
                )}
                <div className="mt-3 flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Analyzing your campaign data...</span>
                </div>
              </>
            ) : (
              // Show waiting message
              <div className="mt-3 flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Target className="w-4 h-4" />
                <span>
                  Waiting for objective and KPI selection...
                </span>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 2: Audience Value Selection
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Select high-value audience segments based on LTV predictions
            </p>
            {stepData?.selectedSegments !== undefined && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-sm">
                  {stepData.selectedSegments}
                </div>
                <span className="text-sm text-gray-900 dark:text-white">
                  segments selected
                </span>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 3: Experiment Setup
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Configure experiment parameters for incrementality testing
            </p>
            {stepData?.experimentEnabled !== undefined && (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Experiment:{" "}
                  </span>
                  <span
                    className={`font-medium ${
                      stepData.experimentEnabled
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {stepData.experimentEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                {stepData?.experimentType && (
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Type:{" "}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {stepData.experimentType}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 4: Media Activation
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Deploying campaigns to media platforms
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-gray-900 dark:text-white">
                  Meta campaign created
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-gray-900 dark:text-white">
                  Google Ads campaign created
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin text-orange-600" />
                <span className="text-gray-900 dark:text-white">
                  Deploying bid rules...
                </span>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 5: Lift Measurement
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Campaign is running. Measurement will begin after cooldown period
              (2-4 weeks)
            </p>
            {stepData?.campaignStatus && (
              <div className="text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Status:{" "}
                </span>
                <span className="font-medium text-green-600">
                  {stepData.campaignStatus}
                </span>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Ant Design Steps Component with ConfigProvider for design system - FIXED AT TOP */}
      <div className="sticky top-0 z-40 px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 shadow-md border-b border-gray-200 dark:border-gray-700">
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#1677ff",
            },
            components: {
              Steps: {
                // Use Ant Design's design tokens
                iconSize: 40,
                iconSizeSM: 32,
              },
            },
          }}
        >
          <Steps
            current={currentStep - 1} // Ant Design uses 0-based index
            items={antSteps}
            size="small"
            labelPlacement="vertical"
            className="performance-agent-steps"
          />
        </ConfigProvider>
      </div>

      {/* Content Area */}
      <div className="space-y-4">
        {/* Step Details */}
        <div className="px-4 pt-4">{renderStepDetails()}</div>

        {/* Step Visualizations - render ALL completed steps stacked vertically */}
        <div className="px-4 space-y-6 pb-6">
        {Object.keys(allStepsData)
          .map(Number)
          .sort((a, b) => a - b)
          .map((stepNum) => {
            const stepData = allStepsData[stepNum];
            const isActiveStep = stepNum === currentStep;

            return (
              <div
                key={stepNum}
                ref={(el) => stepRefs.current[stepNum] = el}
                className={`rounded-lg border-2 transition-all ${
                  isActiveStep
                    ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                    : 'border-gray-200 dark:border-gray-700 opacity-75'
                }`}
              >
                {/* Step Header */}
                <div className={`px-6 py-4 border-b flex items-center justify-between ${
                  isActiveStep
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                      isActiveStep
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}>
                      {stepNum}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {stepData.type === "channel_recommendations" ? "PLAN" :
                         stepData.type === "audience_value_table" ? "BUY" :
                         (stepData.type === "experiment_setup" || stepData.type === "experiment_design" || stepData.type === "power_curve") ? "EXPERIMENT" :
                         stepData.type === "activation_progress" ? "ACT" :
                         stepData.type === "measurement_results" ? "MEASURE" : `Step ${stepNum}`}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {stepData.type === "channel_recommendations" && "Channel budget allocation"}
                        {stepData.type === "audience_value_table" && "Audience segment optimization"}
                        {(stepData.type === "experiment_setup" || stepData.type === "experiment_design" || stepData.type === "power_curve") && "Test design & power analysis"}
                        {stepData.type === "activation_progress" && "Campaign activation"}
                        {stepData.type === "measurement_results" && "Incrementality measurement"}
                      </p>
                    </div>
                  </div>
                  {isActiveStep ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500 text-white animate-pulse">
                      ● Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      ✓ Completed
                    </span>
                  )}
                </div>

                {/* Step Content */}
                <div className="p-6">
                  {stepData.type === "channel_recommendations" &&
                    renderChannelRecommendations(stepData.data)}
                  {stepData.type === "audience_value_table" &&
                    renderAudienceValueTable(stepData.data)}
                  {(stepData.type === "experiment_setup" || stepData.type === "experiment_design" || stepData.type === "power_curve") &&
                    renderExperimentSetup(stepData.data)}
                  {stepData.type === "activation_progress" &&
                    renderActivationProgress(stepData.data)}
                  {stepData.type === "measurement_results" &&
                    renderMeasurementResults(stepData.data)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PerformanceAgentSteps;
